import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  currentCostOfGoodsQueryOptions,
  costOfGoodsDetailsExportQueryOptions,
  costOfGoodsDetailsRawExportQueryOptions,
  costOfGoodsDetailsSearchSchema,
  useCostOfGoodsById,
  useUpdateCostOfGoods,
} from '@/hooks/queries/crt/useCostOfGoods'
import { useLastUpdatedCostOfGoodsLog } from '@/hooks/queries/useAuditTrails'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateCostOfGoods } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CostOfGoodsDetailsDataTable from './-components/CostOfGoodsDetails/CostOfGoodsDetailsDataTable'

export const Route = createFileRoute('/_auth/_modules/crt/cost-of-goods/$year/$month')({
  component: CostOfGoodsPeriod,
  validateSearch: (search) => costOfGoodsDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    const crtPeriod = crtPeriodFromParams(params)
    const costOfGoods = crtPeriod
      ? await queryClient.fetchQuery(currentCostOfGoodsQueryOptions(crtPeriod))
      : null

    return {
      crumb: costOfGoods?.name ?? `${params.year}/${params.month}`,
      crtPeriod,
      costOfGoodsId: costOfGoods ? String(costOfGoods.id) : null,
    }
  },
})

function CostOfGoodsPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { crtPeriod, costOfGoodsId } = Route.useLoaderData()

  if (!costOfGoodsId) {
    return (
      <CrtImportPeriodNotFound
        period={crtPeriod ? formatMonthYear(parseDateOnly(crtPeriod)) : `${year}/${month}`}
        otherPeriodsTo="/crt/cost-of-goods"
      />
    )
  }

  return <CostOfGoodsDetails key={costOfGoodsId} costOfGoodsId={costOfGoodsId} />
}

function CostOfGoodsDetails({ costOfGoodsId }: { costOfGoodsId: string }) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { data, isPending, isError } = useCostOfGoodsById(costOfGoodsId, {
    ...search,
    page: search.page ?? 1,
    pageSize,
  })
  const { data: lastUpdatedCostOfGoodsTrail } = useLastUpdatedCostOfGoodsLog(costOfGoodsId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateCostOfGoods | null => {
    if (!data) return null

    const changes: UpdateCostOfGoods = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedCostOfGoodsTrail)

  const { mutate: updateCostOfGoods, isPending: isUpdatePending } = useUpdateCostOfGoods({
    costOfGoodsId: Number(costOfGoodsId),
  })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateCostOfGoods(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.costOfGoods.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <HStack gap="sm" className="items-start justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_cost_of_goods_edit"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {t('crtPeriod', { ns: 'crt' })}: {formatMonthYear(parseDateOnly(data.crtPeriod))}
          </Text>

          <HStack gap="sm">
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('export.viewData', { ns: 'common' })}
                  description={t('export.viewDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      costOfGoodsDetailsExportQueryOptions(costOfGoodsId, search),
                    )
                  }
                  rowCount={data.detailsRowCount}
                />,
                <ExportCsvMenuItem
                  label={t('export.rawData', { ns: 'common' })}
                  description={t('export.rawDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      costOfGoodsDetailsRawExportQueryOptions(costOfGoodsId),
                    )
                  }
                  rowCount={data.detailsRawRowCount}
                />,
              ]}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/cost-of-goods"
              uploadType="import-cost-of-goods-details"
              uploadAttributes={{ 'cost-of-goods-id': costOfGoodsId }}
            />
          </HStack>
        </VStack>
      </HStack>

      <CostOfGoodsDetailsDataTable
        costOfGoodsId={costOfGoodsId}
        costOfGoods={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_cost_of_goods_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
